import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
} from '@mui/material';
import { useToast } from './Toast';
import { BusinessTrial, updateBusinessTrial } from '../hooks/useBusinesses';
import { colors } from '../theme/theme';

interface NumField {
  key: 'trial_days' | 'provider_count' | 'token_count';
  label: string;
  help: string;
  min: number;
  max: number;
}

const FIELDS: NumField[] = [
  { key: 'trial_days', label: 'Trial length (days)', help: 'Counted from the trial start date, not from today.', min: 1, max: 365 },
  { key: 'provider_count', label: 'Providers included', help: 'Team members assignable to queues.', min: 1, max: 100 },
  { key: 'token_count', label: 'Tokens included', help: 'Tokens per day across all queues.', min: 1, max: 100000 },
];

interface Props {
  open: boolean;
  businessId: string;
  trial: BusinessTrial;
  onClose: () => void;
  onSaved: () => void;
}

/** Number of days the trial currently spans (start → end, inclusive of end). */
const spanDays = (t: BusinessTrial): number => {
  const start = new Date(t.start_date).getTime();
  const end = new Date(t.end_date).getTime();
  const days = Math.round((end - start) / 86400000);
  return days > 0 ? days : 1;
};

const TrialEditDialog: React.FC<Props> = ({ open, businessId, trial, onClose, onSaved }) => {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({
    trial_days: String(spanDays(trial)),
    provider_count: String(trial.provider_count),
    token_count: String(trial.token_count),
  });

  const errors = useMemo(
    () => FIELDS.map((f) => {
      const raw = draft[f.key];
      if (raw.trim() === '') {
        return 'Required';
      }
      const n = Number(raw);
      return !Number.isInteger(n) || n < f.min || n > f.max
        ? `Enter a whole number between ${f.min} and ${f.max}`
        : null;
    }),
    [draft]
  );
  const invalid = errors.some((e) => e !== null);

  // New end date implied by the entered length, so the admin sees the effect.
  const newEnd = useMemo(() => {
    const days = Number(draft.trial_days);
    if (!Number.isInteger(days) || days < 1) {
      return null;
    }
    const d = new Date(trial.start_date);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  }, [draft.trial_days, trial.start_date]);

  const handleSave = async () => {
    if (invalid) {
      return;
    }
    setSaving(true);
    const msg = await updateBusinessTrial(businessId, {
      trial_days: Number(draft.trial_days),
      provider_count: Number(draft.provider_count),
      token_count: Number(draft.token_count),
    });
    setSaving(false);
    toast(msg ?? 'Trial updated', msg ? 'error' : 'success');
    if (!msg) {
      onSaved();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>Edit Free Trial</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 2.5 }}>
          Applies to this business only. Provider and token limits take effect immediately; platform defaults for new
          businesses are set under <b>Trial Settings</b>.
        </Typography>

        <Box sx={{ display: 'grid', gap: 2.25 }}>
          {FIELDS.map((f, i) => (
            <TextField
              key={f.key}
              label={f.label}
              value={draft[f.key]}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value.replace(/[^0-9]/g, '') })}
              error={errors[i] !== null}
              helperText={errors[i] ?? f.help}
              inputProps={{ inputMode: 'numeric' }}
              size="small"
              fullWidth
            />
          ))}
        </Box>

        <Box sx={{ mt: 2.5, p: 1.75, borderRadius: '10px', bgcolor: colors.gray50 }}>
          <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
            Started {trial.start_date} · currently ends {trial.end_date}
            {newEnd && newEnd !== trial.end_date && (
              <> · will end <Box component="span" sx={{ fontWeight: 700, color: colors.textPrimary }}>{newEnd}</Box></>
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: colors.textSecondary }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || invalid}>
          {saving ? 'Saving…' : 'Save trial'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialEditDialog;
